import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Facebook, Search, Star } from "lucide-react";
import { pricingData } from "../data/pricing";
import { FACEBOOK_URL } from "../site";

const categories = ["Tout", ...pricingData.map((c) => c.category)];

export default function Pricing() {
  const [active, setActive] = useState("Tout");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pricingData
      .filter((c) => active === "Tout" || c.category === active)
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (it) => !q || `${it.name} ${it.desc}`.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [active, query]);

  return (
    <section id="tarifs" data-testid="pricing-section" className="bg-[#F0F4F8] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 max-w-2xl"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-teal-700 sm:text-sm">
            Tarifs
          </p>
          <h2 className="font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Une grille claire, sans surprise
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Tous nos prix sont affichés, article par article. Recherchez votre
            textile ou filtrez par catégorie.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 flex flex-col gap-5"
        >
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="pricing-search-input"
              placeholder="Rechercher un article… (ex : canapé, matelas, moquette)"
              className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-12 pr-5 text-sm text-slate-900 shadow-sm outline-none transition-all placeholder:text-slate-400 focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
            />
          </div>

          <div className="flex flex-wrap gap-2.5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`pricing-tab-${cat === "Tout" ? "tout" : cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  active === cat
                    ? "bg-slate-900 text-white shadow-md"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-teal-300 hover:text-teal-700"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="popLayout">
          {filtered.length === 0 ? (
            <motion.p
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="pricing-no-result"
              className="rounded-3xl border border-dashed border-slate-300 bg-white/60 p-12 text-center text-slate-500"
            >
              Aucune prestation ne correspond à votre recherche. Une surface
              introuvable ? Écrivez-nous sur Facebook.
            </motion.p>
          ) : (
            filtered.map((cat) => (
              <motion.div
                key={cat.category}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="border-b border-slate-100 bg-gradient-to-r from-teal-50 to-transparent px-6 py-5 sm:px-8">
                  <h3 className="font-serif text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                    {cat.category}
                  </h3>
                </div>
                <ul>
                  {cat.items.map((item, idx) => (
                    <li
                      key={item.name}
                      data-testid="pricing-item-row"
                      className={`flex flex-col gap-3 px-6 py-5 transition-colors duration-150 hover:bg-teal-50/50 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-8 ${
                        idx !== cat.items.length - 1 ? "border-b border-slate-100" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <p className="font-semibold text-slate-900">{item.name}</p>
                          {item.popular && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-teal-800">
                              <Star className="h-3 w-3 fill-teal-600 text-teal-600" />
                              Populaire
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-500">{item.desc}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-4 sm:flex-col sm:items-end sm:gap-1.5">
                        <span className="font-serif text-2xl font-semibold text-teal-700">
                          {item.price}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                          <Clock className="h-3.5 w-3.5" />
                          {item.turnaround}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex flex-col items-center justify-between gap-6 rounded-3xl bg-[#0B131E] p-8 text-center sm:p-10 lg:flex-row lg:text-left"
        >
          <div>
            <h3 className="font-serif text-2xl font-semibold text-white sm:text-3xl">
              Un grand volume, une tache rebelle ?
            </h3>
            <p className="mt-2 max-w-xl text-sm text-slate-300 sm:text-base">
              Grande pièce, tache rebelle, surface très sale ou volume
              important : envoyez-nous une photo sur Facebook, nous vous
              répondons avec un tarif précis.
            </p>
          </div>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-facebook-quote-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1877F2] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#166FE5] hover:shadow-xl"
          >
            <Facebook className="h-4 w-4" />
            Demander via Facebook
          </a>
        </motion.div>
      </div>
    </section>
  );
}
